import { ShieldAlert } from "lucide-react";
import { IMAGES } from "@/lib/images";
import ParallaxImage from "@/components/ParallaxImage";
import { LuxeLink } from "@/components/LuxeLink";
import { Container, Overline, SectionHeading, Reveal } from "@/components/primitives";

const CATEGORIES = [
  { n: "01", t: "Property Excellence", d: "Architecture, design, bedrooms, bathrooms, amenities, outdoor spaces and maintenance." },
  { n: "02", t: "Location & Privacy", d: "Location, views, privacy, accessibility and arrival experience." },
  { n: "03", t: "Hospitality", d: "Communication, check-in, guest support and professionalism." },
  { n: "04", t: "Guest Experience", d: "Personalisation, welcome experience and attention to detail." },
  { n: "05", t: "Services & Experiences", d: "Transfer, boat, chef, wine, wellness and trusted local partners." },
  { n: "06", t: "Digital Readiness", d: "Digital guest journey, communication and VillaFlow integration." },
];

const NON_NEGOTIABLES = [
  "Serious recurring reputation issues",
  "Unacceptable cleanliness or hygiene",
  "Serious maintenance or safety concerns",
  "Unreliable management",
  "Misleading property information",
  "Partners that could damage guest experience",
  "Fundamental incompatibility with VillaFlow positioning",
];

export default function SelectionStandard() {
  return (
    <div className="relative z-10">
      <section className="pt-40 md:pt-52 pb-24" data-testid="selection-standard-page">
        <Container>
          <div className="max-w-4xl">
            <Reveal>
              <Overline>Assessment</Overline>
              <SectionHeading className="mt-8">
                The VillaFlow Selection Standard<span className="text-gold align-super text-2xl">™</span>
              </SectionHeading>
              <p className="mt-8 text-ivory/65 text-lg md:text-xl font-light leading-relaxed">
                Exceptional properties. Exceptional hospitality. Exceptional experiences. Every property
                is assessed through six areas before it can join the Collection.
              </p>
            </Reveal>
          </div>
        </Container>
      </section>

      <Container className="pb-28">
        <div className="grid md:grid-cols-2 gap-px bg-ivory/10 border border-ivory/10">
          {CATEGORIES.map((c, i) => (
            <Reveal key={c.n} delay={(i % 2) * 0.08}>
              <div className="bg-navy-900 p-10 md:p-14 h-full group hover:bg-navy-800 transition-colors duration-700">
                <div className="flex items-baseline justify-between">
                  <span className="font-serif text-gold/40 text-4xl group-hover:text-gold/70 transition-colors duration-700">{c.n}</span>
                </div>
                <h3 className="font-serif text-3xl text-ivory mt-6">{c.t}</h3>
                <p className="mt-4 text-ivory/55 font-light leading-relaxed max-w-md">{c.d}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <div className="mt-16 text-center max-w-2xl mx-auto">
            <p className="text-ivory/60 font-light leading-relaxed">
              VillaFlow does not publish a numerical score. Assessment is an internal selection tool.
              The public result is simply:
            </p>
            <p className="mt-6 font-serif text-3xl md:text-4xl text-gold tracking-tight">VillaFlow Selected</p>
          </div>
        </Reveal>
      </Container>

      {/* Non-negotiables */}
      <section className="relative" data-testid="non-negotiables">
        <ParallaxImage src={IMAGES.brightInterior} alt="" height="min-h-[80vh]" overlay="bg-navy-900/85">
          <Container className="h-full flex items-center">
            <div className="max-w-3xl py-28">
              <Reveal>
                <div className="inline-flex items-center gap-3 text-gold mb-6"><ShieldAlert size={18} /><span className="text-[11px] tracking-[0.3em] uppercase">Non-Negotiable Standards</span></div>
                <h2 className="font-serif font-light text-3xl md:text-5xl text-ivory tracking-tight leading-tight">
                  Some standards cannot be compromised.
                </h2>
                <p className="mt-6 text-ivory/65 font-light leading-relaxed max-w-xl">
                  A property may be declined regardless of its overall assessment if it presents any of
                  the following. This is not about scoring — it is about trust.
                </p>
                <ul className="mt-10 grid sm:grid-cols-2 gap-x-10 gap-y-4">
                  {NON_NEGOTIABLES.map((n) => (
                    <li key={n} className="flex items-start gap-3 text-ivory/70 text-sm font-light">
                      <span className="mt-2 w-1 h-1 bg-gold rounded-full shrink-0" />{n}
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>
          </Container>
        </ParallaxImage>
      </section>

      <section className="py-28 md:py-40 text-center">
        <Container>
          <Reveal>
            <p className="font-serif font-light text-3xl md:text-5xl text-ivory max-w-3xl mx-auto leading-tight tracking-tight">
              We don't represent every villa.
              <br /><span className="text-gold italic">We represent the right ones.</span>
            </p>
            <div className="mt-12 flex flex-col sm:flex-row justify-center gap-4">
              <LuxeLink to="/apply" variant="solid">Request Consideration</LuxeLink>
              <LuxeLink to="/invitation" variant="outline">I Have an Invitation</LuxeLink>
            </div>
          </Reveal>
        </Container>
      </section>
    </div>
  );
}
